import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { AiUsageCompanyFeatureModelModel, AiUsageCompanyLimitModel, AiUsageCompanyModelModel, ArkUserType } from '../../../core/api/models';
import { AiUsageApiService } from '../../../core/api/services';
import { Utils } from '../../../core/utils';
import { AuthService } from '../../../main/auth.service';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
  selector: 'app-client-ai-usage',
  templateUrl: './client-ai-usage.component.html',
  standalone: false
})
export class ClientAIUsageComponent implements OnInit {
  currentItem?: AiUsageCompanyLimitModel;
  loading = true;
  readonly aiModelPricingLink = ['/ai-model-pricing'];
  private readonly preferredDefaultModelIDs: Record<string, string> = {
    LittleTiff: 'moonshotai.kimi-k2.5',
    NoteReview: 'us.anthropic.claude-opus-5',
    NoteRewrite: 'us.anthropic.claude-opus-5',
    GrammarFix: 'moonshotai.kimi-k2.5'
  };

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private apiService: AiUsageApiService) {
  }

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Support, ArkUserType.Admin]);
    this.route.paramMap.subscribe((params: ParamMap) => {
      const clientID = +(params.get('ID') || '0');
      if (clientID > 0)
        this.load(clientID);
    });
  }

  save(): void {
    if (!this.currentItem)
      return;

    (this.currentItem.Features || []).forEach(feature => this.onFeatureModelAllowedChange(feature));
    this.loading = true;
    this.apiService.SaveCompanyLimit({ body: this.currentItem }).subscribe({
      next: saved => {
        this.currentItem = saved;
        this.loading = false;
        this.alertService.success('AI usage company limits saved successfully');
      },
      error: err => {
        this.loading = false;
        this.alertService.error(Utils.getAjaxError(err));
      }
    });
  }

  isModelAvailable(model: AiUsageCompanyModelModel): boolean {
    return !!model.Active
      && model.InputUsdPerMillionTokens !== null
      && model.OutputUsdPerMillionTokens !== null;
  }

  isGlobalPricingConfigured(model: AiUsageCompanyModelModel): boolean {
    return model.InputUsdPerMillionTokens !== null
      && model.OutputUsdPerMillionTokens !== null;
  }

  hasModelsNeedingGlobalPricing(): boolean {
    return (this.currentItem?.Features || []).some(feature =>
      (feature.Models || []).some(model => !this.isGlobalPricingConfigured(model)));
  }

  getAllowedModels(feature: AiUsageCompanyFeatureModelModel): AiUsageCompanyModelModel[] {
    return (feature.Models || []).filter(model => model.Allowed && this.isModelAvailable(model));
  }

  onFeatureModelAllowedChange(feature: AiUsageCompanyFeatureModelModel): void {
    const allowedModels = this.getAllowedModels(feature);
    if (allowedModels.length === 0) {
      feature.DefaultModelID = null;
      return;
    }

    const currentDefaultIsAllowed = allowedModels.some(model => model.ModelID === feature.DefaultModelID);
    if (!currentDefaultIsAllowed)
      feature.DefaultModelID = this.getPreferredDefaultModelID(feature, allowedModels);
  }

  private load(clientID: number): void {
    this.loading = true;
    this.apiService.GetCompanyLimit({ clientID }).subscribe({
      next: item => {
        this.currentItem = item;
        (this.currentItem.Features || []).forEach(feature => this.onFeatureModelAllowedChange(feature));
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        this.alertService.error(Utils.getAjaxError(err));
      }
    });
  }

  private getPreferredDefaultModelID(
    feature: AiUsageCompanyFeatureModelModel,
    allowedModels: AiUsageCompanyModelModel[]): string | null | undefined {
    const preferredModelID = this.preferredDefaultModelIDs[feature.Feature || ''];
    return allowedModels.find(model => model.ModelID === preferredModelID)?.ModelID
      || allowedModels[0].ModelID;
  }
}
